import type { SupabaseClient } from '@supabase/supabase-js';
import { awaitJob } from '../../lib/await-job.js';
import { openModal } from '@vario-connect/frontend-design';

interface DialogCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  unit: {
    storefront: string;
    idUnit: string;
    ean: string | null;
    title: string | null;
    /** Wenn gesetzt: Unit ist schon einem Vario-Article zugewiesen. Aktiviert die Aufheben-Aktion. */
    varioArticleId?: string | null;
    varioArticleNumber?: string | null;
  };
  /** Called after the assignment job completes successfully so the grid can reload that row. */
  onDone: () => void;
}

interface VarioArticleMatch {
  id: string;
  number: string | null;
  description: string | null;
  ean: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;

export function openArticleDialog(ctx: DialogCtx): void {
  const isMapped = !!ctx.unit.varioArticleId;
  const mappedNumber = ctx.unit.varioArticleNumber ?? ctx.unit.varioArticleId ?? '—';
  const titleLabel = ctx.unit.title ?? '(ohne Titel)';

  const meta = `
    <div class="kl-modal-meta">
      <strong>SKU ${escapeHtml(ctx.unit.idUnit)}</strong>
      <div class="text-grey">${escapeHtml(titleLabel)}${ctx.unit.ean ? ` · EAN ${escapeHtml(ctx.unit.ean)}` : ''}</div>
    </div>
  `;

  const body = isMapped
    ? `
      ${meta}
      <div class="kl-mapped-card">
        <div class="kl-mapped-card-icon"><span class="v-icon fa-light fa-link"></span></div>
        <div class="kl-mapped-card-body">
          <div class="kl-mapped-card-title">Aktuell zugeordnet</div>
          <div class="kl-mapped-card-num text-mono">${escapeHtml(mappedNumber)}</div>
        </div>
        <button type="button" class="v-button kl-mapped-unassign" data-action="unassign">
          <span class="v-icon fa-light fa-xmark"></span>
          Zuordnung aufheben
        </button>
      </div>
      <div class="v-banner v-banner-warning">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>Beim Aufheben wird der Sales-Channel <strong>Kaufland ${escapeHtml(ctx.unit.storefront.toUpperCase())}</strong> in Vario am Artikel deaktiviert. Bestände werden nicht mehr gesynct.</div>
      </div>
      <div class="kl-modal-error" id="kl-art-error" hidden></div>
    `
    : `
      ${meta}
      <div class="kl-form-row">
        <label>Vario-Artikel suchen</label>
        <div class="v-input kl-search-input">
          <span class="v-icon fa-light fa-magnifying-glass"></span>
          <input id="kl-art-search" type="search" placeholder="EAN, SKU oder Bezeichnung …" autocomplete="off" />
          <button type="button" id="kl-art-search-clear" class="kl-search-clear" aria-label="Suche zurücksetzen" hidden>
            <span class="v-icon fa-light fa-xmark"></span>
          </button>
        </div>
        <div class="kl-form-hint">Vorbelegt mit der EAN dieser Position. Mindestens 3 Zeichen.</div>
      </div>
      <div class="kl-search-results" id="kl-art-results"></div>
      <div class="kl-modal-error" id="kl-art-error" hidden></div>
    `;

  const modal = openModal({
    title: isMapped ? 'Vario-Artikel-Zuordnung' : 'Vario-Artikel zuordnen',
    icon: 'fa-link',
    width: 640,
    body,
    // Im nicht-zugeordneten Zweig zusätzlich „Artikel in Vario anlegen" (links): legt für
    // genau diese Kaufland-Unit einen neuen Vario-Artikel an (gleicher Worker-Pfad wie die
    // Bulk-Anlage, _create_if_missing=true). Spart den Umweg über die Bulk-Selektion.
    footer: isMapped
      ? `<button type="button" class="v-button" data-modal-cancel>Schließen</button>`
      : `<button type="button" class="v-button v-button--primary" data-action="create">
           <span class="v-icon fa-light fa-plus"></span>
           Artikel in Vario anlegen
         </button>
         <button type="button" class="v-button" data-modal-cancel>Schließen</button>`,
  });

  const errorEl = modal.el.querySelector<HTMLDivElement>('#kl-art-error')!;

  const showError = (msg: string) => {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };
  const clearError = () => {
    errorEl.textContent = '';
    errorEl.hidden = true;
  };

  if (isMapped) {
    const unassignBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="unassign"]')!;
    unassignBtn.addEventListener('click', () => void unassign(unassignBtn));
  } else {
    const searchInput = modal.el.querySelector<HTMLInputElement>('#kl-art-search')!;
    const clearBtn = modal.el.querySelector<HTMLButtonElement>('#kl-art-search-clear')!;
    const resultsEl = modal.el.querySelector<HTMLDivElement>('#kl-art-results')!;

    const updateClearVisibility = () => { clearBtn.hidden = searchInput.value.length === 0; };

    let debounceTimer: number | null = null;
    searchInput.addEventListener('input', () => {
      updateClearVisibility();
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => void runSearch(searchInput.value, resultsEl), SEARCH_DEBOUNCE_MS) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      searchInput.value = '';
      updateClearVisibility();
      resultsEl.innerHTML = renderEmptyHint();
      searchInput.focus();
    });

    if (ctx.unit.ean) {
      searchInput.value = ctx.unit.ean;
      updateClearVisibility();
      void runSearch(ctx.unit.ean, resultsEl);
    } else {
      resultsEl.innerHTML = renderEmptyHint();
    }
    setTimeout(() => { searchInput.focus(); searchInput.select(); }, 50);
  }

  // „Artikel in Vario anlegen" (Footer, nur im nicht-zugeordneten Zweig vorhanden).
  const createBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="create"]');
  createBtn?.addEventListener('click', () => void create(createBtn));

  async function runSearch(term: string, resultsEl: HTMLDivElement) {
    const trimmed = term.trim();
    if (trimmed.length === 0) {
      resultsEl.innerHTML = renderEmptyHint();
      return;
    }
    if (trimmed.length < 3) {
      resultsEl.innerHTML = `
        <div class="kl-search-empty">
          <span class="v-icon fa-light fa-circle-info"></span>
          Mindestens 3 Zeichen eingeben.
        </div>
      `;
      return;
    }
    resultsEl.innerHTML = `
      <div class="kl-search-loading">
        <span class="v-icon fa-regular fa-spinner-third fa-spin"></span>
        Suche in Vario …
      </div>
    `;
    clearError();
    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-search-articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ search: trimmed, limit: 20 }),
      });
      const body = (await res.json()) as { articles?: VarioArticleMatch[]; error?: { message: string } };
      if (!res.ok || !body.articles) {
        throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      }
      renderResults(body.articles, resultsEl);
    } catch (e) {
      showError(`Suche fehlgeschlagen: ${(e as Error).message}`);
      resultsEl.innerHTML = '';
    }
  }

  function renderResults(articles: VarioArticleMatch[], resultsEl: HTMLDivElement): void {
    if (articles.length === 0) {
      resultsEl.innerHTML = `
        <div class="v-banner v-banner-info">
          <span class="v-icon fa-light fa-circle-info"></span>
          <div>
            <strong>Keine Treffer.</strong> Lege über „Artikel in Vario anlegen" (unten) einen
            neuen Vario-Artikel für diese Position an.
          </div>
        </div>
      `;
      return;
    }
    resultsEl.innerHTML = articles.map((a) => `
      <button type="button" class="kl-search-result" data-action="assign" data-id="${escapeHtml(a.id)}" data-number="${escapeHtml(a.number ?? '')}">
        <div class="kl-search-result-main">
          <div class="kl-search-result-title">${escapeHtml(a.description ?? '(ohne Bezeichnung)')}</div>
          <div class="kl-search-result-meta">
            <span class="text-mono">${escapeHtml(a.number ?? '—')}</span>
            <span class="text-grey">· EAN <span class="text-mono">${escapeHtml(a.ean ?? '—')}</span></span>
          </div>
        </div>
        <span class="v-icon fa-light fa-arrow-right kl-search-result-arrow"></span>
      </button>
    `).join('');
    resultsEl.querySelectorAll<HTMLButtonElement>('button[data-action="assign"]').forEach((btn) => {
      btn.addEventListener('click', () => void assign(btn.dataset['id']!, btn.dataset['number'] || null, btn));
    });
  }

  function renderEmptyHint(): string {
    return `
      <div class="kl-search-empty">
        <span class="v-icon fa-light fa-magnifying-glass"></span>
        Suchbegriff eingeben — EAN, SKU oder Bezeichnung.
      </div>
    `;
  }

  async function assign(varioArticleId: string, varioArticleNumber: string | null, btn: HTMLButtonElement): Promise<void> {
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.innerHTML = `
      <div class="kl-search-result-main">
        <div class="kl-search-result-title"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Zuweisen …</div>
      </div>
    `;
    clearError();
    try {
      // number ins draft mitschicken — der Worker schreibt sie ins
      // article_mappings.vario_article_number-Feld, das die UI in der Spalte
      // "Vario-SKU" anzeigt. Ohne diese Zeile bleibt die Spalte leer fuer
      // alle existing-article-Zuordnungen.
      const draft = varioArticleNumber ? { number: varioArticleNumber } : {};
      const { data, error } = await ctx.supabase.rpc('vario_enqueue_article_assign', {
        _storefront: ctx.unit.storefront,
        _id_unit: ctx.unit.idUnit,
        _vario_article_id: varioArticleId,
        _create_if_missing: false,
        _draft: draft,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Zuweisen fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.innerHTML = originalHtml;
    }
  }

  /**
   * Legt für diese Kaufland-Unit einen NEUEN Vario-Artikel an (kein Match nötig). Nutzt
   * denselben Worker-Job wie die Bulk-Anlage (`vario.article_assign_or_create`,
   * `_create_if_missing=true`, ohne `_vario_article_id`). Der Worker zieht Bezeichnung/EAN/
   * Preis/Artikelnummer aus `kaufland.units` selbst (Draft optional leer) und schreibt das
   * Mapping zurück. Artikel-Import läuft über die Batch-Script-Pipeline → längeres Timeout.
   */
  async function create(btn: HTMLButtonElement): Promise<void> {
    const restoreHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Anlegen …';
    clearError();
    try {
      const { data, error } = await ctx.supabase.rpc('vario_enqueue_article_assign', {
        _storefront: ctx.unit.storefront,
        _id_unit: ctx.unit.idUnit,
        _vario_article_id: null,
        _create_if_missing: true,
        _draft: {},
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId, 180_000);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Anlegen fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
      btn.innerHTML = restoreHtml;
    }
  }

  async function unassign(unassignBtn: HTMLButtonElement): Promise<void> {
    if (!window.confirm(
      `Vario-Zuordnung wirklich aufheben? Das Listing am Sales-Channel "Kaufland ${ctx.unit.storefront.toUpperCase()}" wird in Vario deaktiviert (Artikel + andere Storefronts bleiben).`,
    )) return;
    const restoreHtml = unassignBtn.innerHTML;
    unassignBtn.disabled = true;
    unassignBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Aufheben …';
    clearError();
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_unassign_vario_listing', {
        _storefront: ctx.unit.storefront,
        _id_unit: ctx.unit.idUnit,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId, 60_000);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Aufheben fehlgeschlagen: ${(e as Error).message}`);
      unassignBtn.disabled = false;
      unassignBtn.innerHTML = restoreHtml;
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
